import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingAbortPaper from "./Reusable/LoadingAbortPaper";
import InternalLink from "./Reusable/InternalLink";

const agenciesOf = (target) =>
  [...new Set(target.reps.map((r) => r.agency_name))].join(", ");

// Match on what someone would type to find a rep: name, email or agency.
const filterOptions = createFilterOptions({
  stringify: (t) => `${t.name} ${t.email} ${agenciesOf(t)}`,
});

/**
 * Shown at login to staff holding central:impersonate: pick a user to view
 * Central as, read-only, or carry on as themselves.
 *
 * @param {object} props
 * @param {object} props.api        the staff member's own api, never a view-as one
 * @param {object} props.ownMe      the staff member's own /central/me
 * @param {boolean} props.canContinueAsSelf
 * @param {string|null} props.error why the last choice could not be opened
 * @param {(target: number|"self") => void} props.onChoose
 */
const ViewAsChooser = ({ api, ownMe, canContinueAsSelf, error, onChoose }) => {
  const [targets, setTargets] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [picked, setPicked] = useState(null);

  useEffect(() => {
    api
      .getViewAsTargets()
      .then(setTargets)
      .catch((err) => setLoadError(err.message));
  }, [api]);

  return (
    <LoadingAbortPaper>
      <Stack spacing={2}>
        <Typography variant="h5">View WRS Central as a user</Typography>
        <Typography variant="body2">
          You are signed in as {ownMe.name} ({ownMe.email}). You can see WRS
          Central exactly as another user sees it. Nothing can be changed while
          you do.
        </Typography>

        {error && <Alert severity="error">{error}</Alert>}
        {loadError && <Alert severity="error">{loadError}</Alert>}

        <Autocomplete
          options={targets ?? []}
          loading={!targets && !loadError}
          value={picked}
          onChange={(e, value) => setPicked(value)}
          getOptionLabel={(t) => t.name}
          isOptionEqualToValue={(a, b) => a.user_id === b.user_id}
          filterOptions={filterOptions}
          renderOption={(props, t) => (
            <li {...props} key={t.user_id}>
              <Box>
                <Typography variant="body2">{t.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {agenciesOf(t)} · {t.email}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="User" autoFocus />
          )}
        />

        <Button
          variant="contained"
          disabled={!picked}
          onClick={() => onChoose(picked.user_id)}
        >
          View as (read-only)
        </Button>
        {canContinueAsSelf && (
          <Button onClick={() => onChoose("self")}>Continue as myself</Button>
        )}
        <InternalLink to="/logout">Log out</InternalLink>
      </Stack>
    </LoadingAbortPaper>
  );
};

export default ViewAsChooser;
